import { useEffect, useRef, useState, useCallback } from 'react'
import io from 'socket.io-client'
import { Session, Message, SocketMessage } from '../types/communication.types'
import { messageEventBus } from '../utils/messageEventBus'

// Tipo do Socket
type SocketType = ReturnType<typeof io>

interface UseWebSocketOptions {
  hotelId: number
  onSessionsUpdate?: (sessions: Session[]) => void
  onNewMessage?: (data: { sessionKey: string; message: Message; unreadCount: number; guestName: string }) => void
  onSessionConnected?: (session: Session) => void
  onSessionDisconnected?: (session: Session) => void
  onMessageTranslated?: (data: { originalText: string; translatedText: string }) => void
  onAISuggestion?: (data: { 
    sessionKey: string; 
    suggestion: string; 
    sentiment?: string;
    messageItems?: Array<{ message: string; imageUrl?: string }>;
    imageUrls?: string[];
    confidence?: number;
    fullResponse?: any;
  }) => void
  autoReconnect?: boolean
}

interface UseWebSocketReturn {
  socket: SocketType | null
  isConnected: boolean
  connectionError: string | null
  sendMessage: (message: SocketMessage) => void
  joinSession: (sessionKey: string) => void
  leaveSession: (sessionKey: string) => void
  translateMessage: (text: string, fromLang: string, toLang: string) => void
  sendVoiceCommand: (command: string, lang: string, sessionKey: string) => void
  reconnect: () => void
  disconnect: () => void
  // 🔄 Novas funções de sincronização
  refreshSessionMessages: (sessionKey: string) => void
  syncSessionMessages: (sessionKey: string) => void
  forceSessionUpdate: (sessionKey: string) => void
}

export const useWebSocket = (options: UseWebSocketOptions): UseWebSocketReturn => {
  const {
    hotelId,
    onSessionsUpdate,
    onNewMessage,
    onSessionConnected,
    onSessionDisconnected,
    onMessageTranslated,
    onAISuggestion,
    autoReconnect = true
  } = options

  const [socket, setSocket] = useState<SocketType | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>()
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 10 // Aumentado para 10 tentativas
  const isConnectingRef = useRef(false)
  const healthCheckIntervalRef = useRef<NodeJS.Timeout>()
  const reconnectDelayRef = useRef(1000) // Delay inicial de 1 segundo

  // Obter token de autenticação
  const getAuthToken = useCallback(() => {
    const loginData = localStorage.getItem('loginInfo_ninegrid_access') || 
                     localStorage.getItem('loginInfo_ninegrid_adm')
    
    if (loginData) {
      try {
        return JSON.parse(loginData).accessToken
      } catch (e) {
        console.error('Erro ao parsing do token:', e)
        return null
      }
    }
    return null
  }, [])

  // Função para conectar ao WebSocket - CORRIGIDA
  const connect = useCallback(async () => {
    // Evitar múltiplas conexões simultâneas
    if (socket?.connected || isConnectingRef.current) {
      console.log('WebSocket já conectado ou conectando...')
      return socket
    }

    const authToken = getAuthToken()
    if (!authToken) {
      setConnectionError('Token de autenticação não encontrado')
      return null
    }

    isConnectingRef.current = true
    console.log('🔄 Conectando ao WebSocket da Central de Comunicação...')

    // 🧪 TESTE DE CONECTIVIDADE ANTES DE CONECTAR
    console.log('🧪 Testando conectividade com backend...')
    
    try {
      const { CommunicationAPI } = await import('../services/communicationApi')
      const connectivityTest = await CommunicationAPI.testWebSocketConnectivity()
      
      if (!connectivityTest.available) {
        console.error('❌ Backend não está disponível:', connectivityTest.error)
        setConnectionError(`Backend indisponível: ${connectivityTest.error}`)
        isConnectingRef.current = false
        return null
      }
      
      console.log('✅ Backend disponível, prosseguindo com conexão WebSocket...')
      
    } catch (testError) {
      console.warn('⚠️ Não foi possível testar conectividade, tentando conexão direta:', testError)
    }

    // 🔧 CORREÇÃO: WebSocket provavelmente não está em /api/socket.io mas em /socket.io
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:7004'
    // Remover /api se existir, pois WebSocket geralmente fica na raiz
    const wsUrl = baseUrl.replace('/api', '')
    
    console.log('🌐 URL WebSocket Base:', baseUrl)
    console.log('🌐 URL WebSocket Corrigida:', wsUrl)
    console.log('🎫 Token (primeiros 50 chars):', authToken.substring(0, 50) + '...')
    console.log('🏨 HotelId:', hotelId)
    
    const newSocket = io(wsUrl, {
      path: '/socket.io',
      forceNew: true,
      query: {
        token: authToken,
        userType: 'agent',
        hotelId: hotelId.toString()
      },
      transports: ['websocket'], // 🔥 WebSocket-only (compatível com WebApp)
      timeout: 20000,
      reconnection: false, // Controle manual de reconexão
      autoConnect: true,
      upgrade: false, // 🔥 Não tentar upgrade (manter WebSocket puro)
      rememberUpgrade: false
    })

    // 🔧 MELHORAR LOGS DE CONEXÃO
    newSocket.on('connect', () => {
      console.log('✅ WebSocket conectado com sucesso!')
      console.log('🆔 Socket ID:', newSocket.id)
      console.log('🎯 [VALIDAÇÃO] WebSocket conectado e pronto para receber mensagens')
      setIsConnected(true)
      setConnectionError(null)
      reconnectAttemptsRef.current = 0
      isConnectingRef.current = false
      
      // 🔥 ADMIN DEBUG: Confirmar que listeners bidirecionais estão ativos
      console.log('🔥 ADMIN: WebSocket conectado - Listeners bidirecionais ATIVOS para receber mensagens do WebApp')
      
      // Emit para confirmar conexão
      newSocket.emit('admin-connected', { hotelId })
      
      // 🎯 TESTE: Emitir evento para verificar se backend está recebendo
      newSocket.emit('admin-ready-for-bidirectional', { 
        hotelId, 
        userType: 'agent',
        message: 'Admin pronto para receber mensagens bidirecionais' 
      })
      
      // 🔥 FORÇAR ENTRADA NA SALA DO HOTEL PARA GARANTIR RECEBIMENTO DE MENSAGENS
      newSocket.emit('join-hotel', { 
        hotelId: hotelId,
        source: 'admin',
        userType: 'agent'
      })
      console.log('🏨 ADMIN: Entrando forçadamente na sala do hotel:', hotelId)
      
      // 🔥 REGISTRAR COMO AGENTE ATIVO PARA RECEBER MENSAGENS
      newSocket.emit('register-agent', {
        hotelId: hotelId,
        agentType: 'admin',
        capabilities: ['receive-messages', 'send-messages', 'bidirectional-sync']
      })
      console.log('👤 ADMIN: Registrado como agente ativo para hotel:', hotelId)
      
      // 💓 HEARTBEAT: Sistema de ping para detectar conexões mortas
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current)
      }
      
      healthCheckIntervalRef.current = setInterval(() => {
        if (newSocket && newSocket.connected) {
          // Ping simples para verificar se conexão está viva
          newSocket.emit('admin-ping', { 
            hotelId, 
            timestamp: Date.now() 
          })
          console.log('💓 HEARTBEAT: Ping enviado')
        } else {
          console.warn('⚠️ HEARTBEAT: Socket desconectado durante ping, limpando interval')
          if (healthCheckIntervalRef.current) {
            clearInterval(healthCheckIntervalRef.current)
            healthCheckIntervalRef.current = undefined
          }
        }
      }, 30000) // Ping a cada 30 segundos
    })

    newSocket.on('disconnect', (reason: string) => {
      console.log('WebSocket desconectado:', reason)
      setIsConnected(false)
      isConnectingRef.current = false
      
      // 💓 HEARTBEAT: Limpar interval de ping quando desconectar
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current)
        healthCheckIntervalRef.current = undefined
        console.log('💓 HEARTBEAT: Interval de ping limpo')
      }
      
      if (reason === 'io server disconnect' || reason === 'transport close') {
        // Servidor desconectou, tentar reconectar manualmente com healthcheck
        if (autoReconnect && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++
          
          // Delay exponencial com jitter (randomização para evitar thundering herd)
          const baseDelay = Math.min(1000 * Math.pow(1.5, reconnectAttemptsRef.current - 1), 30000)
          const jitter = Math.random() * 1000 // 0-1000ms de jitter
          const delay = baseDelay + jitter
          
          console.log(`🔄 Agendando tentativa de reconexão ${reconnectAttemptsRef.current}/${maxReconnectAttempts} em ${Math.round(delay)}ms`)
          
          reconnectTimeoutRef.current = setTimeout(async () => {
            console.log(`🔄 Iniciando tentativa de reconexão ${reconnectAttemptsRef.current}/${maxReconnectAttempts}`)
            
            // 🩺 HEALTH CHECK antes de tentar reconectar
            try {
              console.log('🩺 Verificando saúde do backend antes de reconectar...')
              const { CommunicationAPI } = await import('../services/communicationApi')
              const healthCheck = await CommunicationAPI.testWebSocketConnectivity()
              
              if (!healthCheck.available) {
                console.warn(`⚠️ Backend ainda não está saudável (tentativa ${reconnectAttemptsRef.current}), reagendando...`)
                
                // Reagendar com delay maior se backend não está saudável
                if (reconnectAttemptsRef.current < maxReconnectAttempts) {
                  reconnectAttemptsRef.current++
                  const retryDelay = Math.min(5000 * reconnectAttemptsRef.current, 60000)
                  
                  reconnectTimeoutRef.current = setTimeout(() => {
                    console.log(`🔄 Retry após health check falhar: ${reconnectAttemptsRef.current}/${maxReconnectAttempts}`)
                    connect()
                  }, retryDelay)
                }
                return
              }
              
              console.log('✅ Backend está saudável, prosseguindo com reconexão...')
              connect()
              
            } catch (healthError) {
              console.warn('⚠️ Erro no health check, tentando reconectar mesmo assim:', healthError)
              connect()
            }
          }, delay)
        } else {
          console.error(`❌ Máximo de tentativas de reconexão atingido (${maxReconnectAttempts}). WebSocket permanecerá desconectado.`)
          setConnectionError(`Falha na reconexão após ${maxReconnectAttempts} tentativas`)
        }
      }
    })

    newSocket.on('connect_error', (error: any) => {
      console.error('❌ Erro de conexão WebSocket:', error)
      console.error('🔍 Detalhes da conexão:')
      console.error('  - URL:', wsUrl)
      console.error('  - Auth token presente:', !!authToken)
      console.error('  - Token válido:', authToken?.length > 100)
      console.error('  - Hotel ID:', hotelId)
      console.error('  - User Type: agent')
      console.error('  - Path: /socket.io')
      console.error('  - Transports: websocket, polling')
      console.error('  - Error type:', error?.type || 'unknown')
      console.error('  - Error message:', error?.message || error?.description || error)
      
      // 🚨 DIAGNÓSTICO ESPECÍFICO
      if (error?.type === 'TransportError') {
        console.error('🚨 DIAGNÓSTICO: Problema de transporte - backend pode estar offline')
      } else if (error?.message?.includes('401') || error?.message?.includes('unauthorized')) {
        console.error('🚨 DIAGNÓSTICO: Problema de autenticação - token pode estar inválido')
      } else if (error?.message?.includes('CORS')) {
        console.error('🚨 DIAGNÓSTICO: Problema de CORS - configuração do backend')
      } else {
        console.error('🚨 DIAGNÓSTICO: Erro genérico - verificar se backend está rodando na porta 7004')
      }
      
      setConnectionError(`Erro de conexão: ${error.message || error.description || error}`)
      setIsConnected(false)
      isConnectingRef.current = false
      
      // Tentar reconectar em caso de erro com health check inteligente
      if (autoReconnect && reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current++
        
        // Delay exponencial com jitter para connect_error também
        const baseDelay = Math.min(2000 * Math.pow(1.5, reconnectAttemptsRef.current - 1), 30000)
        const jitter = Math.random() * 1000
        const delay = baseDelay + jitter
        
        console.log(`� Reagendando tentativa após connect_error ${reconnectAttemptsRef.current}/${maxReconnectAttempts} em ${Math.round(delay)}ms`)
        
        reconnectTimeoutRef.current = setTimeout(async () => {
          // 🩺 HEALTH CHECK antes de tentar reconectar após erro
          try {
            console.log('🩺 Health check após connect_error...')
            const { CommunicationAPI } = await import('../services/communicationApi')
            const healthCheck = await CommunicationAPI.testWebSocketConnectivity()
            
            if (healthCheck.available) {
              console.log('✅ Backend saudável após erro, reconectando...')
              connect()
            } else {
              console.warn('⚠️ Backend ainda não está saudável após erro, aguardando mais...')
              // Reagendar com delay ainda maior
              if (reconnectAttemptsRef.current < maxReconnectAttempts) {
                const retryDelay = Math.min(10000 * reconnectAttemptsRef.current, 60000)
                reconnectTimeoutRef.current = setTimeout(connect, retryDelay)
              }
            }
          } catch (healthError) {
            console.warn('⚠️ Health check falhou após connect_error, tentando mesmo assim:', healthError)
            connect()
          }
        }, delay)
      } else {
        console.error(`❌ Máximo de tentativas atingido após connect_error (${maxReconnectAttempts})`)
        setConnectionError(`Falha na conexão após ${maxReconnectAttempts} tentativas`)
      }
    })

    // 💓 HEARTBEAT: Listener para pong response
    newSocket.on('admin-pong', (data: any) => {
      console.log('💓 HEARTBEAT: Pong recebido', data)
    })

    // Eventos específicos da Central de Comunicação
    newSocket.on('agent-connected', (data: any) => {
      console.log('Agente conectado:', data)
    })

    newSocket.on('active-sessions', (data: { sessions: Session[] }) => {
      const sessionsCount = data?.sessions?.length || 0
      console.log('Sessões ativas recebidas:', sessionsCount)
      
      // 🛡️ VALIDAÇÃO: Filtrar sessões inválidas (com dados undefined)
      const validSessions = (data?.sessions || []).filter(session => {
        const isValid = session.sessionKey && 
                       session.guestName && 
                       session.hotelId &&
                       session.id
        
        if (!isValid) {
          console.error('❌ [active-sessions] Sessão INVÁLIDA detectada:', {
            sessionKey: session.sessionKey,
            guestName: session.guestName,
            hotelId: session.hotelId,
            id: session.id,
            sessãoCompleta: session
          })
        }
        
        return isValid
      })
      
      if (validSessions.length < sessionsCount) {
        console.warn(`⚠️ [active-sessions] ${sessionsCount - validSessions.length} sessão(ões) inválida(s) filtrada(s)`)
      }
      
      console.log(`✅ [active-sessions] ${validSessions.length} sessão(ões) válida(s) processada(s)`)
      onSessionsUpdate?.(validSessions)
    })

    newSocket.on('guest-message', (data: { 
      sessionKey: string; 
      message: Message; 
      unreadCount: number; 
      guestName: string 
    }) => {
      console.log('Nova mensagem do hóspede:', data)
      onNewMessage?.(data)
    })

    newSocket.on('new-message', (message: Message & { sessionKey?: string; hotelId?: number }) => {
      console.log('🔥 ADMIN: new-message recebido:', message)
      
      // Filtrar mensagens apenas para o hotel correto
      if (message.hotelId && message.hotelId !== hotelId) {
        console.log('🚫 ADMIN: Mensagem ignorada - hotel diferente:', message.hotelId, 'vs', hotelId)
        return
      }
      
      // Se tem sessionKey, pode ser passada como nova mensagem do hóspede
      if (message.sessionKey) {
        onNewMessage?.({
          sessionKey: message.sessionKey,
          message: message,
          unreadCount: 1, // Incrementar contador
          guestName: (message as any).guestName || 'Hóspede'
        })
        console.log('✅ ADMIN: Mensagem do hóspede adicionada via new-message')
      }
    })

    // 🔥 LISTENERS PARA SINCRONIZAÇÃO BIDIRECIONAL - WEBAPP → ADMIN
    
    // Função de validação inteligente de sessão (similar ao WebApp)
    const isValidSessionForMessage = (messageSessionId: string, currentSessionKey: string, messageHotelId?: number): boolean => {
      // Match exato
      if (messageSessionId === currentSessionKey) {
        console.log('✅ ADMIN VALIDATION: Match exato de sessão')
        return true
      }
      
      // Match por inclusão (sessionKey complexo inclui sessionId simples)
      if (currentSessionKey && currentSessionKey.includes(messageSessionId)) {
        console.log('✅ ADMIN VALIDATION: Match por inclusão de sessão')
        return true
      }
      
      // Match inverso (sessionId complexo inclui sessionKey simples)
      if (messageSessionId && messageSessionId.includes(currentSessionKey)) {
        console.log('✅ ADMIN VALIDATION: Match inverso de sessão')
        return true
      }
      
      // Match por hotel (para sincronização bidirecional)
      if (messageHotelId && messageHotelId === hotelId) {
        console.log('✅ ADMIN VALIDATION: Match por hotel para sincronização')
        return true
      }
      
      console.log('🚫 ADMIN VALIDATION: Nenhum match encontrado:', {
        messageSessionId,
        currentSessionKey,
        messageHotelId,
        currentHotelId: hotelId
      })
      return false
    }

    // Listener para mensagens bidirecionais do WebApp
    newSocket.on('bidirectional-message', (messageData: any) => {
      console.log('🔥 ADMIN: bidirectional-message recebido do WebApp:', messageData)
      
      const sessionId = messageData.sessionId || messageData.sessionKey || messageData.session_key
      
      if (isValidSessionForMessage(sessionId, '', messageData.hotelId)) {
        onNewMessage?.({
          sessionKey: sessionId,
          message: messageData,
          unreadCount: 1,
          guestName: messageData.guestName || 'Hóspede WebApp'
        })
        console.log('✅ ADMIN: Mensagem bidirecional do WebApp adicionada')
      } else {
        console.log('🚫 ADMIN: Mensagem bidirecional ignorada (validação falhou)')
      }
    })

    // Listener para mensagens específicas do WebApp
    newSocket.on('webapp-message', (messageData: any) => {
      console.log('🔥 ADMIN: webapp-message recebido:', messageData)
      
      const sessionId = messageData.sessionId || messageData.sessionKey || messageData.session_key
      
      if (isValidSessionForMessage(sessionId, '', messageData.hotelId)) {
        onNewMessage?.({
          sessionKey: sessionId,
          message: messageData,
          unreadCount: 1,
          guestName: messageData.guestName || 'Hóspede WebApp'
        })
        console.log('✅ ADMIN: Mensagem do WebApp adicionada')
      } else {
        console.log('🚫 ADMIN: Mensagem do WebApp ignorada (validação falhou)')
      }
    })

    // Listener para capturar mensagens de hóspede do WebApp
    newSocket.on('guest-message-from-webapp', (messageData: any) => {
      console.log('🔥 ADMIN: guest-message-from-webapp recebido:', messageData)
      
      const sessionId = messageData.sessionId || messageData.sessionKey || messageData.session_key
      
      if (isValidSessionForMessage(sessionId, '', messageData.hotelId)) {
        onNewMessage?.({
          sessionKey: sessionId,
          message: messageData.message || messageData,
          unreadCount: 1,
          guestName: messageData.guestName || messageData.message?.guestName || 'Hóspede WebApp'
        })
        console.log('✅ ADMIN: Mensagem de hóspede do WebApp adicionada')
      } else {
        console.log('🚫 ADMIN: Mensagem de hóspede ignorada (validação falhou)')
      }
    })

    newSocket.on('session-connected', (session: Session) => {
      console.log('Sessão conectada:', session)
      if (session) {
        onSessionConnected?.(session)
      }
    })

    newSocket.on('session-disconnected', (session: Session) => {
      console.log('Sessão desconectada:', session)
      if (session) {
        onSessionDisconnected?.(session)
      }
    })

    newSocket.on('agent-session-joined', (data: { sessionKey: string; messages: Message[] }) => {
      console.log('Agente entrou na sessão:', data?.sessionKey || 'unknown')
    })

    // 🔄 NOVOS HANDLERS DE SINCRONIZAÇÃO (conforme backend atualizado)
    newSocket.on('admin-refresh-session', (data: { 
      sessionKey: string; 
      messages: Message[]; 
      sessionInfo?: any;
      triggerSource?: string;
      messagesCount?: number;
    }) => {
      console.log('🔄 ADMIN SYNC: Refresh de sessão recebido:', data)
      
      // 🔥 FORÇAR REFRESH COMPLETO quando receber admin-refresh-session
      // Isso garante que as novas mensagens sejam carregadas
      if (data?.sessionKey) {
        console.log('🔄 ADMIN SYNC: Forçando refresh completo da sessão:', data.sessionKey)
        
        // Disparar evento global para forçar reload das sessões
        window.dispatchEvent(new CustomEvent('websocket-session-refresh', {
          detail: { sessionKey: data.sessionKey, triggerSource: data.triggerSource || 'unknown' }
        }))
      }
      
      // Callback para atualizar mensagens da sessão específica (se existirem)
      if (onNewMessage && data?.messages?.length > 0) {
        data.messages.forEach(message => {
          onNewMessage({
            sessionKey: data.sessionKey,
            message: message,
            unreadCount: 0, // Não incrementar contador em refresh
            guestName: (message as any).guestName || 'Hóspede'
          })
        })
      }
      
      console.log('✅ ADMIN SYNC: Sessão sincronizada com', data?.messages?.length || 0, 'mensagens')
    });

    newSocket.on('messages-sync-update', (data: {
      sessionKey: string;
      action: 'new' | 'update' | 'refresh';
      message?: Message;
      messages?: Message[];
    }) => {
      console.log('🔄 ADMIN SYNC: Atualização de sincronização recebida:', data)
      
      if (data?.action === 'new' && data?.message) {
        // Nova mensagem individual
        onNewMessage?.({
          sessionKey: data.sessionKey || 'unknown',
          message: data.message,
          unreadCount: 1,
          guestName: (data.message as any).guestName || 'Hóspede'
        })
        console.log('✅ ADMIN SYNC: Nova mensagem sincronizada')
        
      } else if (data?.action === 'refresh' && data?.messages) {
        // Refresh completo das mensagens
        data.messages.forEach(message => {
          onNewMessage?.({
            sessionKey: data.sessionKey || 'unknown',
            message: message,
            unreadCount: 0, // Não incrementar em refresh
            guestName: (message as any).guestName || 'Hóspede'
          })
        })
        console.log('✅ ADMIN SYNC: Mensagens refreshed -', data.messages?.length || 0, 'mensagens')
      }
    })

    newSocket.on('session-messages-updated', (data: {
      sessionKey: string;
      newMessage?: Message;
      totalMessages?: number;
      lastActivity?: string;
    }) => {
      console.log('🔄 ADMIN SYNC: Messages updated para sessão:', data?.sessionKey || 'unknown')
      
      if (data?.newMessage) {
        onNewMessage?.({
          sessionKey: data.sessionKey || 'unknown',
          message: data.newMessage,
          unreadCount: 1,
          guestName: (data.newMessage as any).guestName || 'Hóspede'
        })
        console.log('✅ ADMIN SYNC: Nova mensagem via session-messages-updated')
      }
    })

    // 🚀 LISTENERS ESPECÍFICOS PARA MENSAGENS EM TEMPO REAL
    // 🔥 EVENTO QUE REALMENTE CHEGA: new-guest-message (conforme logs)
    newSocket.on('new-guest-message', (data: any) => {
      console.log('🔥🔥🔥 [MENSAGEM REAL] new-guest-message capturada no ADMIN:', {
        data,
        sessionKey: data?.sessionKey || data?.sessionId || data?.session_key,
        messageContent: data?.message?.content || data?.message?.text || data?.text,
        hasOnNewMessage: !!onNewMessage,
        timestamp: new Date().toISOString()
      })
      
      try {
        // Processar mensagem do evento new-guest-message
        const sessionKey = data?.sessionKey || data?.sessionId || data?.session_key
        const message = data?.message || data
        
        if (sessionKey && message) {
          const messageData = {
            sessionKey: sessionKey,
            message: message,
            unreadCount: data.unreadCount || 1,
            guestName: data.guestName || data.guest || message.guestName || 'Hóspede'
          }
          
          console.log('✅ [new-guest-message] CHAMANDO onNewMessage com:', messageData)
          onNewMessage?.(messageData);
          
          // 🚀 PUBLICAR NO EVENT BUS para useMessages receber automaticamente
          console.log('📡 [new-guest-message] Publicando no messageEventBus:', {
            sessionKey,
            messageId: message.id,
            author: message.author
          })
          messageEventBus.publish({
            sessionKey: sessionKey,
            message: message,
            guestName: data.guestName || data.guest || message.guestName || 'Hóspede',
            unreadCount: data.unreadCount || 1
          })
          
          console.log(`✅ [new-guest-message] Processada para ${sessionKey}`)
        } else {
          console.error('❌ [new-guest-message] Dados inválidos:', {
            hasSessionKey: !!sessionKey,
            hasMessage: !!message,
            data
          })
        }
      } catch (error) {
        console.error('❌ Erro ao processar new-guest-message:', error)
      }
    })

    // 🔥 MANTER listeners originais por garantia (caso backend mude)
    newSocket.on('multi-session-message', (data: any) => {
      console.log('�🔥🔥 [MENSAGEM CHEGOU] multi-session-message capturada no ADMIN:', {
        data,
        sessionKey: data?.sessionKey,
        messageContent: data?.message?.content || data?.message?.text || data?.message,
        hasOnNewMessage: !!onNewMessage,
        timestamp: new Date().toISOString()
      })
      
      try {
        // Processar mensagem do evento multi-session-message
        if (data?.sessionKey && data?.message) {
          const messageData = {
            sessionKey: data.sessionKey,
            message: data.message,
            unreadCount: data.unreadCount || 1,
            guestName: data.guestName || data.guest || 'Hóspede'
          }
          
          console.log('✅ [multi-session-message] CHAMANDO onNewMessage com:', messageData)
          onNewMessage?.(messageData);
          
          // 🚀 PUBLICAR NO EVENT BUS para useMessages receber automaticamente
          console.log('📡 [multi-session-message] Publicando no messageEventBus:', {
            sessionKey: data.sessionKey,
            messageId: data.message.id,
            author: data.message.author
          })
          messageEventBus.publish({
            sessionKey: data.sessionKey,
            message: data.message,
            guestName: data.guestName || data.guest || 'Hóspede',
            unreadCount: data.unreadCount || 1
          })
          
          console.log(`✅ [multi-session-message] Processada para ${data.sessionKey}`)
        } else {
          console.error('❌ [multi-session-message] Dados inválidos:', {
            hasSessionKey: !!data?.sessionKey,
            hasMessage: !!data?.message,
            data
          })
        }
      } catch (error) {
        console.error('❌ Erro ao processar multi-session-message:', error)
      }
    })

    newSocket.on('force-refresh-messages', (data: any) => {
      console.log('�🔥🔥 [REFRESH FORÇADO] force-refresh-messages capturada no ADMIN:', {
        data,
        sessionKey: data?.sessionKey,
        hasOnNewMessage: !!onNewMessage,
        timestamp: new Date().toISOString()
      })
      
      try {
        // Processar mensagem do evento force-refresh-messages
        if (data?.sessionKey && data?.message) {
          const messageData = {
            sessionKey: data.sessionKey,
            message: data.message,
            unreadCount: data.unreadCount || 1,
            guestName: data.guestName || data.guest || 'Hóspede'
          }
          
          console.log('✅ [force-refresh-messages] CHAMANDO onNewMessage com:', messageData)
          onNewMessage?.(messageData);
          
          console.log(`✅ [force-refresh-messages] Processada para ${data.sessionKey}`)
        } else {
          console.log('⚠️ [force-refresh-messages] Sem mensagem específica, forçando apenas refresh')
        }
        
        // Forçar refresh das sessões
        onSessionsUpdate?.([])
      } catch (error) {
        console.error('❌ Erro ao processar force-refresh-messages:', error)
      }
    })

    newSocket.on('message-translated', (data: { 
      originalText: string; 
      translatedText: string;
      fromLang: string;
      toLang: string;
    }) => {
      console.log('Mensagem traduzida:', data)
      onMessageTranslated?.(data)
    })

    // 🔥 LISTENERS CRÍTICOS PARA RECEBER MENSAGENS DO WEBAPP
    // Capturar TODOS os possíveis eventos que o backend pode estar enviando
    const criticalEvents = [
      'message', 'webapp-message', 'hotel-message', 'session-message', 
      'message-to-admin', 'message-from-guest', 'guest-to-admin',
      'client-message', 'user-message', 'chat-message'
    ]
    
    criticalEvents.forEach(eventName => {
      newSocket.on(eventName, (data: any) => {
        console.log(`🔥 ADMIN CRITICAL: Evento ${eventName} recebido:`, data)
        
        // Tentar processar como nova mensagem independente do formato
        try {
          // Extrair dados essenciais do evento
          const sessionId = data.sessionId || data.sessionKey || data.session_key || data.session
          const message = data.message || data
          const hotelId = data.hotelId || data.hotel_id
          const guestName = data.guestName || data.guest_name || data.from || 'Hóspede'
          
          console.log(`🔥 ADMIN CRITICAL: Dados extraídos:`, {
            sessionId,
            message: message?.text || message,
            hotelId,
            guestName,
            originalEvent: eventName
          })
          
          // Se temos dados válidos, processar como nova mensagem
          if (sessionId && message) {
            onNewMessage?.({
              sessionKey: sessionId,
              message: typeof message === 'string' ? { text: message, author: 'guest' } : message,
              unreadCount: 1,
              guestName: guestName
            })
            console.log(`✅ ADMIN CRITICAL: Mensagem do evento ${eventName} processada com sucesso`)
          }
        } catch (error) {
          console.error(`❌ ADMIN CRITICAL: Erro ao processar evento ${eventName}:`, error)
        }
      })
    })
    
    console.log('🔥 ADMIN CRITICAL: Todos os listeners críticos configurados para receber mensagens')

    // 🤖 LISTENERS PARA EVENTOS DA IA (modo assistido e autopilot)
    newSocket.on('ai-suggestion', (data: any) => {
      console.log('🤖 [AI-SUGGESTION] Sugestão da IA recebida:', {
        sessionKey: data?.sessionKey,
        suggestion: data?.suggestion?.substring(0, 100),
        messageItems: data?.messageItems?.length || 0,
        imageUrls: data?.imageUrls?.length || 0,
        sentiment: data?.sentiment,
        timestamp: new Date().toISOString()
      })
      
      try {
        if (data?.sessionKey && data?.suggestion) {
          console.log(`✅ [AI-SUGGESTION] Sugestão recebida para sessão ${data.sessionKey}`)
          
          // 🔥 CRITICAL: Chama callback para processar sugestão no useCommunicationCenter
          if (onAISuggestion) {
            console.log('🔄 [AI-SUGGESTION] Enviando sugestão para callback handler com messageItems')
            onAISuggestion({
              sessionKey: data.sessionKey,
              suggestion: data.suggestion,
              messageItems: data.messageItems, // ✅ NOVO: Preservar array com message + imageUrl
              imageUrls: data.imageUrls, // ✅ NOVO: Array de URLs para compatibilidade
              sentiment: data.sentiment,
              confidence: data.confidence,
              fullResponse: data.fullResponse // ✅ NOVO: Dados completos para fallback
            })
          } else {
            console.warn('⚠️ [AI-SUGGESTION] Callback onAISuggestion não definido!')
          }
        } else {
          console.warn('⚠️ [AI-SUGGESTION] Dados incompletos:', data)
        }
      } catch (error) {
        console.error('❌ Erro ao processar ai-suggestion:', error)
      }
    })

    newSocket.on('ai-auto-response', (data: any) => {
      console.log('🤖 [AI-AUTO-RESPONSE] Resposta automática da IA recebida:', {
        sessionKey: data?.sessionKey,
        messageItems: data?.messageItems?.length || 0,
        imageUrls: data?.imageUrls?.length || 0,
        sentiment: data?.sentiment,
        timestamp: new Date().toISOString()
      })
      
      try {
        if (data?.sessionKey && data?.aiResponse) {
          const messageData = {
            sessionKey: data.sessionKey,
            message: {
              ...data.aiResponse,
              imageUrl: data.messageItems?.[0]?.imageUrl, // ✅ Adicionar primeira imagem do batch
              text: data.aiText,
            },
            unreadCount: 0, // Resposta automática não incrementa contador
            guestName: data.guestName || data.guest || 'Hóspede'
          }
          
          console.log('✅ [AI-AUTO-RESPONSE] CHAMANDO onNewMessage com:', messageData)
          onNewMessage?.(messageData);
          
          // 🚀 PUBLICAR NO EVENT BUS para useMessages receber automaticamente
          console.log('📡 [AI-AUTO-RESPONSE] Publicando no messageEventBus')
          messageEventBus.publish({
            sessionKey: data.sessionKey,
            message: {
              ...data.aiResponse,
              imageUrl: data.messageItems?.[0]?.imageUrl, // ✅ Primeira imagem
              text: data.aiText,
            },
            guestName: data.guestName || data.guest || 'Hóspede',
            unreadCount: 0
          })
          
          console.log(`✅ [AI-AUTO-RESPONSE] Resposta automática processada para ${data.sessionKey}`)
        } else {
          console.error('❌ [AI-AUTO-RESPONSE] Dados inválidos:', {
            hasSessionKey: !!data?.sessionKey,
            hasAiResponse: !!data?.aiResponse,
            data
          })
        }
      } catch (error) {
        console.error('❌ Erro ao processar ai-auto-response:', error)
      }
    })

    console.log('🤖 ADMIN: Listeners para eventos da IA configurados (ai-suggestion, ai-auto-response)')

    // 📋 LISTAR TODOS OS EVENTOS REGISTRADOS
    console.log('📋 [WebSocket] Resumo de listeners registrados:', {
      mensagensDoGuest: ['new-guest-message', 'multi-session-message', 'force-refresh-messages'],
      eventosCriticos: [
        'message', 'webapp-message', 'hotel-message', 'session-message', 
        'message-to-admin', 'message-from-guest', 'guest-to-admin',
        'client-message', 'user-message', 'chat-message'
      ],
      eventosIA: ['ai-suggestion', 'ai-auto-response'],
      eventosAdmin: ['bidirectional-message', 'active-sessions', 'session-connected', 'session-disconnected'],
      total: 20
    })

    // 🐛 LISTENER DE DEBUG GLOBAL - Capturar TODOS os eventos para debugging
    const enableDebug = process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG_WEBSOCKET === 'true'
    if (enableDebug) {
      console.log('🐛 ADMIN DEBUG: Configurando listener global para todos os eventos...')
      
      // Socket.IO v2.x não tem onAny - usar listeners específicos para debug
      console.log('🐛 ADMIN DEBUG: Configurando listeners específicos para Socket.IO v2.x');
      
      // Método 2: Override do onevent para compatibilidade
      if ((newSocket as any).onevent) {
        const originalOnevent = (newSocket as any).onevent
        ;(newSocket as any).onevent = function (packet: any) {
          try {
            console.log('🔥 ADMIN DEBUG: onevent interceptado:', {
              type: packet?.type || 'unknown',
              data: packet?.data || null,
              timestamp: new Date().toLocaleTimeString(),
              packetExists: !!packet
            })
            
            // 🚀 FILTRO ESPECÍFICO para eventos de mensagem
            if (packet?.data && Array.isArray(packet.data) && packet.data.length >= 2) {
              const eventName = packet.data[0]
              const eventData = packet.data[1]
              
              console.log(`🎯 [${eventName}] Evento específico capturado:`, {
                eventName,
                eventData,
                isMultiSessionMessage: eventName === 'multi-session-message',
                isForceRefresh: eventName === 'force-refresh-messages',
                sessionKey: eventData?.sessionKey,
                hasMessage: !!eventData?.message
              })
              
              // Se for um evento de mensagem que precisamos capturar
              if (eventName === 'multi-session-message' || eventName === 'force-refresh-messages') {
                console.log(`🚨 [${eventName}] EVENTO DE MENSAGEM DETECTADO! Dados:`, eventData)
              }
            }
            
          } catch (debugError) {
            console.error('❌ Erro no debug onevent:', debugError)
          }
          
          // Chamar método original
          return originalOnevent.call(this, packet)
        }
      }
      
      console.log('✅ ADMIN DEBUG: Listeners de debug configurados')
    }

    setSocket(newSocket)
    return newSocket
  }, [hotelId, getAuthToken, autoReconnect, onSessionsUpdate, onNewMessage, onSessionConnected, onSessionDisconnected, onMessageTranslated, onAISuggestion]) // Adicionado onAISuggestion

  // Função para enviar mensagem
  const sendMessage = useCallback((message: SocketMessage) => {
    if (socket?.connected) {
      console.log('Enviando mensagem:', message)
      socket.emit('send-message', message)
    } else {
      console.error('Socket não conectado')
      setConnectionError('Socket não conectado')
    }
  }, [socket])

  // Função para entrar em uma sessão
  const joinSession = useCallback((sessionKey: string) => {
    if (socket?.connected) {
      console.log('Entrando na sessão:', sessionKey)
      socket.emit('agent-join-session', { sessionKey, hotelId })
    } else {
      console.error('Socket não conectado')
    }
  }, [socket, hotelId])

  // Função para sair de uma sessão
  const leaveSession = useCallback((sessionKey: string) => {
    if (socket?.connected) {
      console.log('Saindo da sessão:', sessionKey)
      // Não há evento específico para sair, mas podemos implementar se necessário
    }
  }, [socket])

  // Função para traduzir mensagem
  const translateMessage = useCallback((text: string, fromLang: string, toLang: string) => {
    if (socket?.connected) {
      console.log('Solicitando tradução:', { text, fromLang, toLang })
      socket.emit('translate-message', { text, fromLang, toLang })
    } else {
      console.error('Socket não conectado')
    }
  }, [socket])

  // Função para comando por voz
  const sendVoiceCommand = useCallback((command: string, lang: string, sessionKey: string) => {
    if (socket?.connected) {
      console.log('Enviando comando por voz:', { command, lang, sessionKey })
      socket.emit('voice-command', { command, lang, sessionKey })
    } else {
      console.error('Socket não conectado')
    }
  }, [socket])

  // 🔄 Função para refresh manual de mensagens (conforme backend atualizado)
  const refreshSessionMessages = useCallback((sessionKey: string) => {
    if (socket?.connected) {
      console.log('🔄 ADMIN: Solicitando refresh manual de mensagens:', sessionKey)
      socket.emit('refresh-admin-messages', { 
        sessionId: sessionKey,
        sessionKey: sessionKey, // Compatibilidade com diferentes formatos
        hotelId: hotelId
      })
    } else {
      console.error('❌ ADMIN: Socket não conectado para refresh')
    }
  }, [socket, hotelId])

  // 🔄 Função para sincronizar todas as mensagens de uma sessão
  const syncSessionMessages = useCallback((sessionKey: string) => {
    if (socket?.connected) {
      console.log('🔄 ADMIN: Sincronizando mensagens da sessão:', sessionKey)
      socket.emit('sync-session-messages', { 
        sessionKey: sessionKey,
        hotelId: hotelId
      })
    } else {
      console.error('❌ ADMIN: Socket não conectado para sync')
    }
  }, [socket, hotelId])

  // 🔄 Função para forçar atualização de sessão específica
  const forceSessionUpdate = useCallback((sessionKey: string) => {
    if (socket?.connected) {
      console.log('🔄 ADMIN: Forçando atualização da sessão:', sessionKey)
      socket.emit('force-session-update', { 
        sessionKey: sessionKey,
        hotelId: hotelId,
        requestType: 'admin-refresh'
      })
    } else {
      console.error('❌ ADMIN: Socket não conectado para force update')
    }
  }, [socket, hotelId])

  // Função para reconectar
  const reconnect = useCallback(() => {
    console.log('Iniciando reconexão manual...')
    disconnect()
    setTimeout(() => {
      reconnectAttemptsRef.current = 0
      connect()
    }, 1000)
  }, []) // Removido connect e disconnect das dependências

  // Função para desconectar
  const disconnect = useCallback(() => {
    console.log('Desconectando WebSocket...')
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
    
    // 💓 HEARTBEAT: Limpar interval de ping na desconexão manual
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current)
      healthCheckIntervalRef.current = undefined
      console.log('💓 HEARTBEAT: Interval limpo na desconexão manual')
    }
    
    if (socket) {
      socket.disconnect()
      setSocket(null)
    }
    
    setIsConnected(false)
    setConnectionError(null)
    isConnectingRef.current = false
  }, [socket])

  // Efeito para inicializar conexão - CORRIGIDO PARA EVITAR MÚLTIPLAS CONEXÕES
  useEffect(() => {
    if (hotelId && !socket && !isConnectingRef.current) {
      const timer = setTimeout(() => {
        if (!socket && !isConnectingRef.current) {
          connect()
        }
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [hotelId])

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      // 💓 HEARTBEAT: Limpeza final no unmount
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current)
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      disconnect()
    }
  }, [])

  return {
    socket,
    isConnected,
    connectionError,
    sendMessage,
    joinSession,
    leaveSession,
    translateMessage,
    sendVoiceCommand,
    reconnect,
    disconnect,
    // 🔄 Novas funções de sincronização
    refreshSessionMessages,
    syncSessionMessages,
    forceSessionUpdate
  }
}